import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const tabCls = (active) =>
  `rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
    active ? "border-brand-emerald bg-brand-emerald/15 text-brand-emerald" : "border-brand-mist/20 text-brand-mist hover:text-brand-snow"
  }`;

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={onClose} data-testid={testid}>
    <div className="h-full w-full max-w-2xl space-y-6 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-brand-snow">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const saveBtn = (saving, label = "Save") => (
  <button type="submit" disabled={saving} className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow disabled:opacity-60">
    {saving ? "Saving…" : label}
  </button>
);

// ---------- Pages (homepage hero, about, and future pages) ----------

const PagesTab = () => {
  const [pages, setPages] = useState([]);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEdit(p);
    setF({ title: p.title, seo_title: p.seo?.title || "", seo_description: p.seo?.description || "", sections: p.sections || {} });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/pages/${edit.key}`, {
        key: edit.key,
        title: f.title,
        sections: f.sections,
        seo: { title: f.seo_title, description: f.seo_description },
        status: edit.status,
      });
      setEdit(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="pages-tab">
      <p className="text-sm text-brand-mist">Edit the homepage hero and About page copy. Changes go live immediately on save.</p>
      <div className="surface-card divide-y divide-brand-mist/10">
        {pages.map((p) => (
          <div key={p.key} className="flex items-center justify-between px-6 py-4" data-testid={`page-row-${p.key}`}>
            <div>
              <p className="text-sm font-medium text-brand-snow">{p.title}</p>
              <p className="text-xs text-brand-mist">key: {p.key} · {p.status}</p>
            </div>
            <button onClick={() => openEdit(p)} data-testid={`page-edit-${p.key}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs font-medium text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
              <Pencil size={12} /> Edit
            </button>
          </div>
        ))}
      </div>
      {edit && f && (
        <Modal title={`Edit page: ${edit.title}`} onClose={() => setEdit(null)} testid="page-editor-modal">
          <form onSubmit={save} className="space-y-5">
            {Object.entries(f.sections).map(([k, v]) => (
              <Field key={k} label={k.replace(/_/g, " ")} htmlFor={`pf-${k}`}>
                <TextArea
                  id={`pf-${k}`}
                  rows={Array.isArray(v) ? Math.min(v.length + 1, 6) : 2}
                  testid={`pf-${k}`}
                  value={Array.isArray(v) ? v.join("\n") : v}
                  onChange={(e) =>
                    setF({ ...f, sections: { ...f.sections, [k]: Array.isArray(v) ? e.target.value.split("\n").filter(Boolean) : e.target.value } })
                  }
                />
              </Field>
            ))}
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="SEO title" htmlFor="pf-seo-title"><TextInput id="pf-seo-title" testid="pf-seo-title" value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} /></Field>
              <Field label="SEO description" htmlFor="pf-seo-desc"><TextInput id="pf-seo-desc" testid="pf-seo-desc" value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })} /></Field>
            </div>
            {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
            {saveBtn(saving)}
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------- FAQs ----------

const EMPTY_FAQ = { question: "", answer: "", category: "General", order: 0, status: "published" };

const FaqsTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(EMPTY_FAQ);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (edit.id) await api.put(`/admin/faqs/${edit.id}`, f);
      else await api.post("/admin/faqs", f);
      setEdit(null);
      load();
    } catch {}
    setSaving(false);
  };

  const remove = async (faq) => { await api.delete(`/admin/faqs/${faq.id}`); load(); };

  return (
    <div className="space-y-4" data-testid="faqs-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-mist">{faqs.length} FAQs shown on the About page.</p>
        <button onClick={() => { setEdit({}); setF(EMPTY_FAQ); }} data-testid="faq-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-4 py-2 text-sm font-semibold text-brand-black hover:bg-brand-snow">
          <Plus size={14} /> Add FAQ
        </button>
      </div>
      <div className="surface-card divide-y divide-brand-mist/10">
        {faqs.map((faq) => (
          <div key={faq.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`faq-row-${faq.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-brand-snow">{faq.question}</p>
              <p className="text-xs text-brand-mist">{faq.category} · {faq.status}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => { setEdit(faq); setF({ question: faq.question, answer: faq.answer, category: faq.category, order: faq.order, status: faq.status }); }} data-testid={`faq-edit-${faq.id}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-snow hover:border-brand-emerald hover:text-brand-emerald"><Pencil size={12} /></button>
              <button onClick={() => remove(faq)} data-testid={`faq-delete-${faq.id}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-mist hover:border-red-400 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <Modal title={edit.id ? "Edit FAQ" : "New FAQ"} onClose={() => setEdit(null)} testid="faq-editor-modal">
          <form onSubmit={save} className="space-y-5">
            <Field label="Question" htmlFor="fq-q" required><TextInput id="fq-q" testid="fq-question" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} required /></Field>
            <Field label="Answer" htmlFor="fq-a" required><TextArea id="fq-a" rows={4} testid="fq-answer" value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} required /></Field>
            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Category" htmlFor="fq-c"><TextInput id="fq-c" testid="fq-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
              <Field label="Order" htmlFor="fq-o"><TextInput id="fq-o" type="number" testid="fq-order" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) || 0 })} /></Field>
              <Field label="Status" htmlFor="fq-s">
                <SelectInput id="fq-s" testid="fq-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </SelectInput>
              </Field>
            </div>
            {saveBtn(saving)}
          </form>
        </Modal>
      )}
    </div>
  );
};

// ---------- Redirects ----------

const RedirectsTab = () => {
  const [rules, setRules] = useState([]);
  const [f, setF] = useState({ from_path: "", to_path: "" });
  const [error, setError] = useState("");

  const load = () => api.get("/admin/redirects").then((r) => setRules(r.data)).catch((err) => setError(formatApiError(err.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/redirects", f);
      setF({ from_path: "", to_path: "" });
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const disable = async (r) => { await api.delete(`/admin/redirects/${r.id}`); load(); };

  return (
    <div className="space-y-4" data-testid="redirects-tab">
      <p className="text-sm text-brand-mist">Map old or moved URLs to their new home. Active rules apply site-wide on next page load.</p>
      <form onSubmit={add} className="flex flex-wrap items-end gap-3">
        <Field label="From path" htmlFor="rd-from"><TextInput id="rd-from" testid="rd-from" value={f.from_path} onChange={(e) => setF({ ...f, from_path: e.target.value })} placeholder="/old-page" required /></Field>
        <Field label="To path or URL" htmlFor="rd-to"><TextInput id="rd-to" testid="rd-to" value={f.to_path} onChange={(e) => setF({ ...f, to_path: e.target.value })} placeholder="/new-page" required /></Field>
        <button type="submit" data-testid="rd-add" className="rounded-full bg-brand-emerald px-5 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Add redirect</button>
      </form>
      {error && <p role="alert" className="text-sm text-red-400" data-testid="redirects-error">{error}</p>}
      <div className="surface-card divide-y divide-brand-mist/10">
        {rules.length === 0 && <p className="px-6 py-5 text-sm text-brand-mist">No redirect rules yet.</p>}
        {rules.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-6 py-4" data-testid={`redirect-row-${r.id}`}>
            <p className="text-sm text-brand-snow">
              <span className="text-brand-mist">{r.from_path}</span> → {r.to_path}
              {!r.active && <span className="ml-2 text-xs text-brand-mist">(inactive)</span>}
            </p>
            {r.active && (
              <button onClick={() => disable(r)} data-testid={`redirect-disable-${r.id}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-mist hover:border-red-400 hover:text-red-400">Disable</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminContent = () => {
  const [tab, setTab] = useState("pages");
  return (
    <div className="space-y-6" data-testid="admin-content">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Site content</h1>
        <p className="text-sm text-brand-mist">Homepage and About copy, site-wide FAQs and URL redirects.</p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="content-tabs">
        {[["pages", "Pages"], ["faqs", "FAQs"], ["redirects", "Redirects"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`content-tab-${k}`} className={tabCls(tab === k)}>{label}</button>
        ))}
      </div>
      {tab === "pages" && <PagesTab />}
      {tab === "faqs" && <FaqsTab />}
      {tab === "redirects" && <RedirectsTab />}
    </div>
  );
};

export default AdminContent;
